"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Calendar, Check } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ContactAppointmentSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="beratung-buchen" className="py-24 bg-[#0A1628] text-white relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`transition-all duration-700 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-[42px] font-extrabold font-default mb-6 text-white tracking-tight leading-tight">
              Kostenloses Erstgespräch buchen
            </h2>
            <p className="text-xl text-white/80 font-default">
              30 Minuten. Unverbindlich. Wir zeigen Ihnen genau, was Ihre Webseite 
              braucht — und was sie kosten würde.
            </p>
          </div>

          <div className="flex flex-col items-center justify-center gap-12">
            <div className="space-y-8 w-full max-w-3xl mx-auto">
              <Card className="border-white/10 shadow-2xl overflow-hidden bg-white/5 backdrop-blur-md text-white h-full flex flex-col rounded-lg">
                <CardHeader className="pb-4 bg-transparent border-b border-white/10">
                  <CardTitle className="flex items-center gap-2 text-white font-default text-xl">
                    <SafeHydrate><Calendar className="w-5 h-5 text-accent" /></SafeHydrate>
                    Online Terminbuchung
                  </CardTitle>
                  <CardDescription className="text-white/60 text-base">
                    Wählen Sie direkt einen freien Slot in unserem Kalender.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow p-0 relative bg-transparent">
                  <div className="w-full h-[600px] relative">
                    <iframe src="https://calendly.com/oscar-martinez-fontenla/30min" width="100%" height="100%" title="Calendly Scheduling Page" className="w-full h-full border-0" />
                  </div>
                  <div className="py-4 border-t border-white/10 text-center">
                    <p className="text-white text-sm font-default">
                      oscar martinez · 30 Minuten · Unverbindlich
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="bg-white/5 border-white/10 shadow-sm backdrop-blur-sm rounded-lg">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="bg-accent/20 p-2 rounded-full shrink-0">
                      <Check className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-bold font-default text-sm mb-1 text-white">100% Kostenlos</h4>
                      <p className="text-xs text-white/60 leading-relaxed">
                        Das Erstgespräch ist völlig unverbindlich und kostenfrei.
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10 shadow-sm backdrop-blur-sm rounded-lg">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="bg-accent/20 p-2 rounded-full shrink-0">
                      <Check className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-bold font-default text-sm mb-1 text-white">24h Bestätigung</h4>
                      <p className="text-xs text-white/60 leading-relaxed">
                        Wir bestätigen Ihren Termin garantiert innerhalb von 24 Stunden.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}